import { UseFormReturn } from 'react-hook-form';
import {
    AttributeOption,
    EventPanelFormType,
    EventVehicleDto,
    StringValueOption,
} from '@mark43/cad-api';
import { DrawerType } from '../../../drawers/types';
import {
    TowVehicleBaseKey,
    EventPanelVehicleBaseKey,
} from '../involved-person-vehicle-fields/types';

/**
 * @param vehicleString formats: [state abbreviation]-[plate], [plate], or [17 character VIN]
 */
export function parseVehicleInformation(
    vehicleString: string
): {
    plateNumber?: string;
    state?: string;
    vinNumber?: string;
} {
    if (vehicleString.length === 17) {
        return { vinNumber: vehicleString };
    } else if (vehicleString.includes('-')) {
        const [state, plateNumber] = vehicleString.split('-');
        return { state, plateNumber };
    } else {
        return { plateNumber: vehicleString };
    }
}

type VehiclesValue = Partial<EventVehicleDto> & {
    plateNumberKey: string;
    addedViaNarrative: boolean;
};

export function createNewVehicleArrayFromNarrative({
    vehicleStrings,
    stateOptions,
    defaultPurposeCode,
    defaultPlateType,
    defaultPlateYear,
    defaultStateOptionValue,
    currentVehicles,
    addedViaNarrative = false,
}: {
    vehicleStrings: string[];
    stateOptions: AttributeOption[];
    defaultPurposeCode?: StringValueOption;
    defaultPlateType?: StringValueOption;
    defaultPlateYear?: string;
    defaultStateOptionValue?: number;
    currentVehicles?: VehiclesValue[];
    addedViaNarrative?: boolean;
}): VehiclesValue[] {
    const currentVehiclesMap = (currentVehicles || ([] as VehiclesValue[])).reduce<
        Record<string, VehiclesValue>
    >((acc, cur) => {
        if (cur.plateNumberKey) {
            acc[cur.plateNumberKey] = cur;
        }
        return acc;
    }, {});
    let defaultStateStr = '';
    const stateMap = stateOptions.reduce<Record<string, AttributeOption>>((acc, cur) => {
        const stateAbbr = cur.label.toUpperCase();
        if (cur.value && cur.value === defaultStateOptionValue) {
            defaultStateStr = stateAbbr;
        }
        acc[stateAbbr] = cur;
        return acc;
    }, {});

    return vehicleStrings.map((vehicleString) => {
        const { plateNumber, state, vinNumber } = parseVehicleInformation(vehicleString);

        const plateNumberKey = vehicleString;
        const currentVehicleData = currentVehiclesMap[plateNumberKey];
        const stateOption =
            currentVehicleData?.stateOption ??
            stateMap[(state ?? defaultStateStr ?? '').toUpperCase()];
        const purposeCode = currentVehicleData?.purposeCode ?? defaultPurposeCode;
        const plateType = currentVehicleData?.plateType ?? defaultPlateType;
        const plateYear = currentVehicleData?.plateYear ?? defaultPlateYear;
        return {
            ...currentVehicleData,
            plateNumber: plateNumber ?? currentVehicleData?.plateNumber,
            vinNumber: vinNumber ?? currentVehicleData?.vinNumber,
            stateOption,
            purposeCode,
            plateType,
            plateYear,
            plateNumberKey,
            addedViaNarrative,
        };
    });
}

export function getInitialValuesForTowVehicle(
    baseKey: EventPanelVehicleBaseKey,
    index: number,
    eventForm: UseFormReturn<EventPanelFormType>
): Partial<EventPanelFormType['towVehicles'][0]> {
    const [
        vinNumber,
        plateNumber,
        stateOption,
        vehicleMakeOption,
        vehicleModelOption,
        colorOption,
        notes,
        year,
    ] = eventForm.getValues([
        `${baseKey}.${index}.vinNumber`,
        `${baseKey}.${index}.plateNumber`,
        `${baseKey}.${index}.stateOption`,
        `${baseKey}.${index}.vehicleMakeOption`,
        `${baseKey}.${index}.vehicleModelOption`,
        `${baseKey}.${index}.colorOption`,
        `${baseKey}.${index}.notes`,
        `${baseKey}.${index}.year`,
    ]);

    return {
        vinNumber: vinNumber ?? null,
        plateNumber: plateNumber ?? null,
        stateOption: stateOption ?? null,
        vehicleMakeOption: vehicleMakeOption ?? null,
        vehicleModelOption: vehicleModelOption ?? null,
        colorOption: colorOption ?? null,
        notes: notes ?? null,
        year: year ?? null,
    };
}

export function resetVehicleFieldsOnTowServiceCancellation({
    baseKey,
    index,
    eventForm,
}: {
    baseKey: TowVehicleBaseKey;
    index: number;
    eventForm: UseFormReturn<EventPanelFormType>;
}) {
    eventForm.setValue(`${baseKey}.${index}.rotationServiceListPositionId`, null, {
        shouldDirty: true,
        shouldValidate: true,
    });
    eventForm.setValue(`${baseKey}.${index}.towTypeOption`, null, {
        shouldDirty: true,
        shouldValidate: true,
    });
    eventForm.setValue(`${baseKey}.${index}.towCompanyOption`, null, {
        shouldDirty: true,
        shouldValidate: true,
    });
    eventForm.setValue(`${baseKey}.${index}.towCompanyOther`, null, {
        shouldDirty: true,
        shouldValidate: true,
    });
    eventForm.setValue(`${baseKey}.${index}.towedToLocation`, null, {
        shouldDirty: true,
        shouldValidate: true,
    });
}

const reopenTowRotationDrawerOnCancellation = (
    baseKey: TowVehicleBaseKey,
    index: number,
    eventForm: UseFormReturn<EventPanelFormType>,
    openDrawer: (drawer: DrawerType<'service-rotation'>) => void
) => {
    const eventId = eventForm.getValues('eventId');

    if (eventId) {
        eventForm.setValue(
            `${baseKey}.${index}.towTypeOption`,
            { value: 'Department', label: 'Department' },
            { shouldDirty: true, shouldValidate: true }
        );

        openDrawer({
            type: 'service-rotation',
            props: {
                eventId,
                towRotationProps: {
                    eventForm,
                    towVehicleBaseKey: baseKey,
                    towVehicleIndex: index,
                },
            },
        });
    }
};

export const onTowRotationCancellation = ({
    baseKey,
    index,
    eventForm,
    openDrawer,
}: {
    baseKey: 'towVehicles' | 'mergedTowVehicles';
    index: number;
    eventForm: UseFormReturn<EventPanelFormType>;
    openDrawer: (drawer: DrawerType<'service-rotation'>) => void;
}) => {
    resetVehicleFieldsOnTowServiceCancellation({ baseKey, index, eventForm });
    reopenTowRotationDrawerOnCancellation(baseKey, index, eventForm, openDrawer);
};
